import { useState } from 'react'
import { isOrgAdmin, type NavData, type NavItem } from '@assessments/shared'
import { resolveActiveNavEntry } from '@/lib/navActive'
import { useSession } from '../../features/auth/SessionContext.js'
import { SearchBar } from './SearchBar.js'
import { NavLockIcon } from './NavLockIcon.js'

interface Props {
  nav: NavData | null
  platformOrigin?: string
}

const accordionHeader =
  'flex w-full items-center justify-between px-4 py-2 font-jakarta text-base font-medium text-body hover:bg-surface-hover'
const childLabel = 'font-jakarta text-sm font-semibold text-heading'
const childDesc = 'mt-0.5 text-xs text-muted'
const directLink =
  'block px-4 py-2 font-jakarta text-base font-medium text-body no-underline hover:bg-surface-hover'
const userLink =
  'block px-4 py-2 text-base font-medium text-subtle no-underline hover:bg-surface-hover hover:text-heading'

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-5 w-5 transition-transform ${open ? 'rotate-180' : ''}`}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function MobileItem({
  item,
  isActive = false,
  activeChildIndex = null,
}: {
  item: NavItem
  isActive?: boolean
  activeChildIndex?: number | null
}) {
  const [open, setOpen] = useState(false)
  // pl-[14px] absorbs the 2px bar out of the usual px-4, keeping labels aligned.
  const activeBar = 'border-l-2 border-highlight bg-surface-hover pl-[14px]'

  if (item.state === 'disabled') {
    return (
      <div
        className={`flex w-full items-center px-4 py-2 font-jakarta text-base font-medium text-subtle ${isActive ? activeBar : ''}`}
      >
        <span>{item.label}</span>
        {item.show_lock && <NavLockIcon />}
      </div>
    )
  }

  if (item.children.length > 0) {
    return (
      <div>
        <button
          type="button"
          aria-current={isActive ? 'page' : undefined}
          className={`${accordionHeader} ${isActive ? activeBar : ''}`}
          onClick={() => setOpen((v) => !v)}
        >
          <span>{item.label}</span>
          <Chevron open={open} />
        </button>
        {open && (
          <div className="space-y-1 pl-4">
            {item.children.map((child, index) => (
              <a
                key={`${child.href}::${child.label}`}
                href={child.href}
                target={child.target}
                rel={child.target === '_blank' ? 'noopener noreferrer' : undefined}
                aria-current={index === activeChildIndex ? 'page' : undefined}
                className={`block py-2 no-underline ${index === activeChildIndex ? `${activeBar} pr-4` : 'px-4'}`}
              >
                <div className={childLabel}>{child.label}</div>
                {child.description && <div className={childDesc}>{child.description}</div>}
              </a>
            ))}
          </div>
        )}
      </div>
    )
  }

  return item.href ? (
    <a
      href={item.href}
      aria-current={isActive ? 'page' : undefined}
      className={`${directLink} ${isActive ? activeBar : ''}`}
    >
      {item.label}
    </a>
  ) : (
    <span className="block px-4 py-2 font-jakarta text-base font-medium text-subtle">
      {item.label}
    </span>
  )
}

/**
 * Mobile nav panel (< lg) that drops below the bar, matching the Platform's `.nav-mobile-menu`:
 * a full-width search, the nav items as accordions/direct links, and a user section with the
 * account links + logout. Desktop uses the horizontal NavBar + avatar UserMenu instead.
 */
export function MobileNav({ nav, platformOrigin }: Props) {
  const { session, logout } = useSession()
  const [loggingOut, setLoggingOut] = useState(false)

  const active = resolveActiveNavEntry(nav?.items ?? [])
  const orgAdmin = isOrgAdmin(session?.user.roles)
  const accountUrl = (path: string) => (platformOrigin ? `${platformOrigin}${path}` : undefined)

  async function handleLogout() {
    setLoggingOut(true)
    await logout()
  }

  return (
    <div className="lg:hidden">
      <div className="max-h-[calc(100vh-4rem)] overflow-y-auto pb-3 pt-2">
        <div className="px-4 pb-2 pt-1">
          <SearchBar search={nav?.search} platformOrigin={platformOrigin} fullWidth />
        </div>
        {nav?.items.map((item, index) => (
          <MobileItem
            key={item.active_route_pattern}
            item={item}
            isActive={active?.itemIndex === index && active.childIndex === null}
            activeChildIndex={active?.itemIndex === index ? active.childIndex : null}
          />
        ))}
      </div>

      <div className="border-t border-border pb-3 pt-4">
        {session && (
          <div className="flex items-center px-4">
            <img className="h-10 w-10 rounded-full" src="/img/user-placeholder.svg" alt="Profile" />
            <div className="ml-3 text-base font-medium text-heading">
              {session.user.name ?? session.user.email}
            </div>
          </div>
        )}
        <div className="mt-3 space-y-1">
          {accountUrl('/account/profile') && (
            <a href={accountUrl('/account/profile')} className={userLink}>
              Account
            </a>
          )}
          {orgAdmin && accountUrl('/account/my-organization') && (
            <a href={accountUrl('/account/my-organization')} className={userLink}>
              My Organization
            </a>
          )}
          {orgAdmin && accountUrl('/account/users') && (
            <a href={accountUrl('/account/users')} className={userLink}>
              Manage Users
            </a>
          )}
          <button
            type="button"
            onClick={() => void handleLogout()}
            disabled={loggingOut}
            className={`${userLink} w-full cursor-pointer border-none bg-transparent text-left disabled:opacity-60`}
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  )
}
